// Client-side "Recommended" for the agenda. No API: it only reads what the app already holds.
//
// Two signals, because the onboarding topics (26 global interests shared by every event) are a
// different vocabulary from the per-event tracks and tags, so topics alone can never be matched by
// id and rarely by exact name (measured on production event 18: 5 of 26 names equal a track name).
//  - Bookmarks: the tracks and tags of the sessions the user bookmarked. Works for every event with
//    no configuration, and gets better the more the user bookmarks.
//  - Onboarding topics: a loose word match against track and tag names, so a user with no bookmarks
//    yet still gets something ("AI Agents" matches the track "AI Agents", "AI Engineering" too).

export type RecommendableSession = {
  id: number,
  track?: {id: number, name: string},
  tags?: string[],
};

const STOP_WORDS = new Set(['and', 'the', 'of', 'for', 'with', 'in', 'to', 'an']);

const tokenize = (text: string): Set<string> => new Set(
  text.toLowerCase().split(/[^a-z0-9.#+]+/).filter(token => token.length > 1 && !STOP_WORDS.has(token))
);

// A topic matches a label when MORE than half of the topic's words appear in it. Exactly half would
// let the word "AI" alone match every "AI ..." track and tag (measured: 244 of 347 sessions on
// production event 18 for the topic "AI Agents").
const MIN_TOPIC_WORD_SHARE = 0.5;

const topicMatchesLabel = (topicTokens: Set<string>, labelTokens: Set<string>): boolean => {
  if (!topicTokens.size) {
    return false;
  }
  let shared = 0;
  topicTokens.forEach((token) => {
    if (labelTokens.has(token)) {
      shared += 1;
    }
  });
  return shared / topicTokens.size > MIN_TOPIC_WORD_SHARE;
};

// A shared track alone is enough to recommend a session.
// A shared tag only counts by how rare it is: "Agentic AI" sits on a third of the WWC26 NA agenda, so
// sharing it says little, while a tag on a handful of sessions says a lot (inverse document frequency).
// Tags alone must add up to more than a track does: two or three moderately rare tags, or one very rare one.
const TRACK_HIT_SCORE = 2.5;
const MIN_SCORE = 2.5;
const MIN_TAG_ONLY_SCORE = 4;

export const recommendedSessionIds = (
  sessions: RecommendableSession[],
  bookmarkedIds: Set<number>,
  topicNames: string[],
): Set<number> => {
  const tagFrequency = new Map<string, number>();
  sessions.forEach(session => (session.tags ?? []).forEach(tag => tagFrequency.set(tag, (tagFrequency.get(tag) ?? 0) + 1)));
  const tagWeight = (tag: string): number => Math.log(sessions.length / (tagFrequency.get(tag) ?? 1));

  const bookmarkedTracks = new Set<number>();
  const bookmarkedTags = new Set<string>();
  sessions.forEach((session) => {
    if (!bookmarkedIds.has(session.id)) {
      return;
    }
    if (session.track?.id) {
      bookmarkedTracks.add(session.track.id);
    }
    (session.tags ?? []).forEach(tag => bookmarkedTags.add(tag));
  });

  const topics = topicNames.map(tokenize).filter(tokens => tokens.size > 0);
  const recommended = new Set<number>();

  sessions.forEach((session) => {
    // Already on the user's agenda, nothing to recommend.
    if (bookmarkedIds.has(session.id)) {
      return;
    }
    let score = 0;
    if (session.track?.id && bookmarkedTracks.has(session.track.id)) {
      score += TRACK_HIT_SCORE;
    } else {
      const tagScore = (session.tags ?? []).reduce((sum, tag) => sum + (bookmarkedTags.has(tag) ? tagWeight(tag) : 0), 0);
      score = tagScore >= MIN_TAG_ONLY_SCORE ? tagScore : 0;
    }
    if (score < MIN_SCORE && topics.length) {
      const labels = [session.track?.name ?? '', ...(session.tags ?? [])].map(tokenize);
      if (topics.some(topic => labels.some(label => topicMatchesLabel(topic, label)))) {
        score = MIN_SCORE;
      }
    }
    if (score >= MIN_SCORE) {
      recommended.add(session.id);
    }
  });

  return recommended;
};
